import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GridViewWebComponent } from '../../../../grid/gridView';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { WEB_COMPONENT_GRID_VIEW } from '../../../../webcomponents.constants';
import { ColumnKeys, LiveSearchResultsGridViewData } from '../types';
import { LiveSearchResultsGridDataProvider } from '../dataProvider/LiveSearchResultsGridDataProvider';
import { defaultPageSizeOptions, ViewConfiguration } from '../../../../grid/gridView/types';
import { TemplateState } from '../../../../types/common/types';
import { generateEndpointLink, isQueryExecutionFinished } from '../../utils';
import { modifyFontForNumbers } from './columnRenderers';

export default class LiveSearchResultsGridView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $gridContainer: HTMLElement;
    $grid: GridViewWebComponent<any, any>;
    $notStartedContainer: HTMLElement;
    $notStartedMessage: GzTitleWebComponent;
    $notStartedAdditionalText: GzTextWebComponent;
    $noResultsContainer: HTMLElement;
    $noResultsMessage: GzTitleWebComponent;
    $inProgressContainer: HTMLElement;
    $inProgressMessage: GzTitleWebComponent;
    $resultsExpiredMessage: GzTitleWebComponent;
    $reRunMessage: GzTextWebComponent;
    $resultsExpiredContainer: HTMLElement;
  };

  public getTranslations(): void {
    this.translations = this.stringsTranslator.getLiveSearchGridStrings();
  }

  public setTranslatedStrings(): void {
    this.dom.$notStartedMessage.innerText = this.translations.notStartedMessage;
    this.dom.$notStartedAdditionalText.innerText = this.translations.notStartedAdditionalText;
    this.dom.$noResultsMessage.innerText = this.translations.noResultsMessage;
    this.dom.$inProgressMessage.innerText = this.translations.inProgressMessage;
    this.dom.$resultsExpiredMessage.innerText = this.translations.resultsExpired;
    this.dom.$reRunMessage.innerText = this.translations.reRunMessage;
  }

  public getDomElements(): void {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$gridContainer = this.shadow.querySelector('.grid');
    this.dom.$grid = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$notStartedContainer = this.shadow.querySelector('#not-started');
    this.dom.$notStartedMessage = this.shadow.querySelector('#not-started-message');
    this.dom.$notStartedAdditionalText = this.shadow.querySelector('#not-started-additional-text');
    this.dom.$reRunMessage = this.shadow.querySelector('#re-run-message');
    this.dom.$noResultsContainer = this.shadow.querySelector('#no-results');
    this.dom.$noResultsMessage = this.shadow.querySelector('#no-results-message');
    this.dom.$inProgressContainer = this.shadow.querySelector('#in-progress');
    this.dom.$inProgressMessage = this.shadow.querySelector('#in-progress-message');
    this.dom.$resultsExpiredMessage = this.shadow.querySelector('#results-expired-message');
    this.dom.$resultsExpiredContainer = this.shadow.querySelector('#results-expired');

    if (this.dom.$grid.injectDependencies) {
      this.dom.$grid.injectDependencies(this.dependencies);
      this.dom.$grid.start();
    }

    this.getTranslations();
    this.setTranslatedStrings();
  }

  public buildSuccessView(data: LiveSearchResultsGridViewData): void {
    this.resetTemplateState(this.dom.$wrapper);

    this.dom.$grid.dataProvider = new LiveSearchResultsGridDataProvider(this.dependencies, data.queryExecution);

    const view: ViewConfiguration<any, any> = data.views[0];

    this.dom.$grid.viewId = view.id;
    this.dom.$grid.filters = view.filters;
    this.dom.$grid.columns = view.displayedColumns;
    this.dom.$grid.pageSize = view.pageSize;
    this.dom.$grid.isCompactViewChecked = view.compactView;
    this.dom.$grid.allColumnDetails = data.allColumns;
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.ENDPOINT]: generateEndpointLink.bind(this),
    };
    for (const details of data.allColumns) {
      if (details.key !== ColumnKeys.ENDPOINT) {
        this.dom.$grid.columnRenderers[details.key] = (modifyFontForNumbers as typeof this.dom.$grid.columnRenderers[string]);
      }
    }
    this.dom.$grid.defaultViewProps = data.defaultViewProps;
    this.dom.$grid.removeFilters = true;
    this.dom.$grid.hideFiltersToggle = true;
    this.dom.$grid.disableGridSettings = true;
    this.dom.$grid.hideHeaderActions = false;
    this.dom.$grid.hideFooter = false;
    this.dom.$grid.hasPagination = true;
    this.dom.$grid.pageSizeOptions = defaultPageSizeOptions;
    this.dom.$grid.propertiesSet = true;

    if (!data.queryExecution) {
      this.showNotStartedPanel();
    } else if (!isQueryExecutionFinished(data.queryExecution) && !data.queryExecution.resultsFields) {
      //doesn't have results
      this.showInProgressPanel();
    } else if (data.queryExecution.resultsFields && !data.queryExecution.hasExpiredResults) {
      //has results and are not expired
      this.showGrid();
    } else if (!data.queryExecution.hasExpiredResults) {
      this.showNoResultsPanel();
    } else {
      this.showResultsExpiredPanel();
    }
  }

  public buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public resetColumns() {
    if (this.dom.$grid) {
      this.dom.$grid.resetColumns();
    }
  }

  private showNotStartedPanel(): void {
    this.setElementClass(this.dom.$gridContainer, 'hidden');
    this.removeElementClass(this.dom.$notStartedContainer, 'hidden');
    this.setElementClass(this.dom.$noResultsContainer, 'hidden');
    this.setElementClass(this.dom.$inProgressContainer, 'hidden');
    this.setElementClass(this.dom.$resultsExpiredContainer, 'hidden');
  }

  private showNoResultsPanel(): void {
    this.setElementClass(this.dom.$gridContainer, 'hidden');
    this.setElementClass(this.dom.$notStartedContainer, 'hidden');
    this.removeElementClass(this.dom.$noResultsContainer, 'hidden');
    this.setElementClass(this.dom.$inProgressContainer, 'hidden');
    this.setElementClass(this.dom.$resultsExpiredContainer, 'hidden');
  }

  private showResultsExpiredPanel(): void {
    this.setElementClass(this.dom.$gridContainer, 'hidden');
    this.setElementClass(this.dom.$notStartedContainer, 'hidden');
    this.setElementClass(this.dom.$noResultsContainer, 'hidden');
    this.setElementClass(this.dom.$inProgressContainer, 'hidden');
    this.removeElementClass(this.dom.$resultsExpiredContainer, 'hidden');
  }

  private showInProgressPanel(): void {
    this.setElementClass(this.dom.$gridContainer, 'hidden');
    this.setElementClass(this.dom.$notStartedContainer, 'hidden');
    this.setElementClass(this.dom.$noResultsContainer, 'hidden');
    this.removeElementClass(this.dom.$inProgressContainer, 'hidden');
    this.setElementClass(this.dom.$resultsExpiredContainer, 'hidden');
  }

  private showGrid(): void {
    this.removeElementClass(this.dom.$gridContainer, 'hidden');
    this.setElementClass(this.dom.$notStartedContainer, 'hidden');
    this.setElementClass(this.dom.$noResultsContainer, 'hidden');
    this.setElementClass(this.dom.$inProgressContainer, 'hidden');
    this.setElementClass(this.dom.$resultsExpiredContainer, 'hidden');
  }
}
